import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatOption, MatSelect } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { PASSWORD_MIN_LENGTH } from '@rms/auth';
import { applyRejectedFieldErrors, setServerError } from '@rms/core/error';
import { NotificationService } from '@rms/core/notification';
import { whitespaceValidator } from '@rms/core/validators';
import { CloseButtonComponent } from '@rms/sdk/dialog';
import { AppError, FieldValidationError } from '@rms/sdk/errors';
import { ControlErrorDirective } from '@rms/sdk/forms/error';
import { MatchValidator, PasswordValidator } from '@rms/sdk/validators';
import { PhoneInputComponent } from '@rms/ui/forms';
import { ToastComponent } from '@rms/ui/toast';
import { USER_EMAIL_MAX_LENGTH, USER_NAME_MAX_LENGTH, USER_PHONE_DEFAULT_COUNTRY } from '@rms/user';
import { BehaviorSubject, finalize, map, Observable, startWith } from 'rxjs';

import { PartnerEmailInUseError } from '../../errors';
import {
  PARTNER_USER_ROLE,
  PARTNER_USER_ROLE_LABELS,
  PartnerRole,
  PartnerUser,
  PartnerUserCreateRequest,
  PartnerUserUpdateRequest,
} from '../../models';
import { PartnerService } from '../../services';
import { PartnerUserDialogData } from '../partner-user-dialog.model';

@Component({
  selector: 'rms-partner-user-dialog',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogClose,
    MatFormField,
    MatSuffix,
    MatInput,
    MatButton,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    MatSelect,
    MatOption,
    TranslatePipe,
    CloseButtonComponent,
    ControlErrorDirective,
    PhoneInputComponent,
    ToastComponent,
  ],
  templateUrl: './partner-user-dialog.component.html',
})
export class PartnerUserDialogComponent implements OnInit {
  protected readonly data = inject<PartnerUserDialogData>(MAT_DIALOG_DATA);
  protected readonly phoneDefaultCountry = USER_PHONE_DEFAULT_COUNTRY;
  protected readonly roleLabels = PARTNER_USER_ROLE_LABELS;
  protected readonly loading$ = new BehaviorSubject<boolean>(false);
  protected readonly error$ = new BehaviorSubject<string | null>(null);
  protected readonly passwordVisible$ = new BehaviorSubject<boolean>(false);
  protected readonly repeatVisible$ = new BehaviorSubject<boolean>(false);
  protected readonly form = inject(FormBuilder).nonNullable.group(
    {
      first_name: ['', [Validators.required, whitespaceValidator, Validators.maxLength(USER_NAME_MAX_LENGTH)]],
      last_name: ['', [whitespaceValidator, Validators.maxLength(USER_NAME_MAX_LENGTH)]],
      email: ['', [Validators.required, Validators.email, Validators.maxLength(USER_EMAIL_MAX_LENGTH)]],
      phone_number: new FormControl<string | null>(null),
      role: new FormControl<string | null>(null, Validators.required),
      password: [
        '',
        [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH), PasswordValidator.StrongPassword],
      ],
      repeat: ['', [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)]],
    },
    {
      validators: MatchValidator.Fields('password', 'repeat', 'auth.controls.repeat-password.error-mismatch'),
    }
  );

  protected readonly changed$: Observable<boolean> = this.form.valueChanges.pipe(
    startWith(null),
    map(() => !this.data.user || !sameRequest(this.updateRequest(), requestOf(this.data.user)))
  );

  protected readonly roles$ = new BehaviorSubject<PartnerRole[]>([]);

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject<MatDialogRef<PartnerUserDialogComponent, PartnerUser>>(MatDialogRef);
  private readonly partnerService = inject(PartnerService);
  private readonly notificationService = inject(NotificationService);

  public ngOnInit(): void {
    const { user } = this.data;

    if (user) {
      this.form.patchValue({ ...user, last_name: user.last_name ?? '' });
      this.form.controls.role.disable();
      this.form.controls.password.disable();
      this.form.controls.repeat.disable();

      return;
    }

    this.partnerService
      .getPartnerRoles(this.data.scope)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (roles) => {
          this.roles$.next(roles);
          this.form.controls.role.setValue(roles.find(({ name }) => name === PARTNER_USER_ROLE.USER)?.id ?? null);
        },
        error: (error: AppError) => this.error$.next(error.message),
      });
  }

  protected submit(): void {
    if (this.form.invalid) {
      return;
    }

    const { scope, user } = this.data;
    const request$ = user
      ? this.partnerService.updatePartnerUser(scope, user.id, this.updateRequest())
      : this.partnerService.createPartnerUser(scope, this.createRequest());
    const successKey = user ? 'partners.users.edit.success.update' : 'partners.users.edit.success.create';
    this.error$.next(null);
    this.loading$.next(true);

    request$
      .pipe(
        finalize(() => this.loading$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (saved) => {
          this.notificationService.notify(successKey, 'common.ok');
          this.dialogRef.close(saved);
        },
        error: (error: AppError) => this.showError(error),
      });
  }

  private createRequest(): PartnerUserCreateRequest {
    const { first_name, last_name, email, phone_number } = this.updateRequest();

    return {
      first_name,
      ...(last_name && { last_name }),
      email,
      ...(phone_number && { phone_number }),
      credentials: { password: this.form.controls.password.value },
      roles: [this.form.controls.role.value ?? ''],
    };
  }

  private updateRequest(): PartnerUserUpdateRequest {
    const { first_name, last_name, email, phone_number } = this.form.getRawValue();

    return {
      first_name: first_name.trim(),
      last_name: last_name.trim() || null,
      email: email.trim(),
      phone_number: phone_number || null,
    };
  }

  private showError(error: AppError): void {
    if (error instanceof PartnerEmailInUseError) {
      setServerError(this.form.controls.email, error.message);

      return;
    }

    if (error instanceof FieldValidationError) {
      applyRejectedFieldErrors(this.form, error);
    }

    this.error$.next(error.message);
  }
}

function requestOf({ first_name, last_name, email, phone_number }: PartnerUser): PartnerUserUpdateRequest {
  return { first_name, last_name: last_name || null, email, phone_number: phone_number || null };
}

function sameRequest(a: PartnerUserUpdateRequest, b: PartnerUserUpdateRequest): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}
